import { uniqBy } from "lodash-es";
import type { ChannelName } from "../../../../modules/channels/types";
import { isEmailChannel } from "../../../../modules/channels/types";
import type {
  GroupedMomentAnalyticsData,
  SentMomentData,
} from "../../../../modules/stats/server/analytics-service";
import {
  filterStatType,
  isMomentAnalyticsStatType,
} from "../../../../modules/stats/server/analytics-service";
import { formatIntervalFromParts } from "../../../../utils/interval";
import { fromISO8601 } from "../../../../utils/iso8601";
import type { UUID } from "../../../../utils/uuid";
import { calculatePercentage } from "../utils";

export type MomentAnalyticsCellDataDisplay =
  | {
      value: number;
      type: "percentage" | "number";
    }
  | {
      type: null;
    };

type SentMomentDataRow = Omit<SentMomentData, "stats">;

export type TransformedGroupedMomentAnalytics = {
  id: number;
  moment_id: UUID;
  moment_grouping_id: UUID | null;
  account_id: UUID;
  min_timestamp: Date;
  recipients: {
    display: string;
    type: "number" | "person_name" | "channel_name" | null;
    cc?: string[];
    bcc?: string[];
    connection_type?: string | null;
  };
  subject: { when: string; subject?: string } | null;
  delivered: MomentAnalyticsCellDataDisplay;
  opened: MomentAnalyticsCellDataDisplay;
  interacted: MomentAnalyticsCellDataDisplay;
  rawData: GroupedMomentAnalyticsData | SentMomentDataRow;
  channel_name: ChannelName;
  has_dynamic_content: boolean;
  poll_ids?: UUID[];
  survey_data?: SentMomentData["survey_data"];
};

export function transformGroupedMomentAnalytics(
  item: GroupedMomentAnalyticsData,
  index: number,
): TransformedGroupedMomentAnalytics {
  const stats = item.stats.filter(
    (stat) =>
      isMomentAnalyticsStatType("delivery")(stat) || isMomentAnalyticsStatType("error")(stat),
  );

  const deliveries = filterStatType("delivery", item.stats);
  const deliveredPercentage = calculatePercentage(deliveries.length, stats.length);

  const opens = filterStatType("grouped_open", item.stats);

  const openPercentage = calculatePercentage(
    item.stats.filter(isMomentAnalyticsStatType("grouped_open")).length,
    deliveries.length,
  );

  const clicks = filterStatType("click", item.stats);

  const uniqueClicks = uniqBy(clicks, (stat) => stat.data.recipient_data?.recipient_id);

  const interactionPercentage = calculatePercentage(
    uniqueClicks.length,
    isEmailChannel(item.channel_name) ? opens.length : deliveries.length,
  );

  return {
    id: index,
    moment_id: item.moment_id,
    moment_grouping_id: item.moment_grouping_id,
    account_id: item.account_id,
    min_timestamp: fromISO8601(item.min_timestamp),
    recipients: makeRecipients(
      item,
      stats.length,
      stats.length === 1 ? (stats[0]!.data.recipient_data ?? null) : null,
    ),
    subject: makeSubject(item),
    delivered: {
      value: deliveredPercentage,
      type: "percentage",
    },
    opened: {
      value: openPercentage,
      type: "percentage",
    },
    interacted: {
      value: interactionPercentage,
      type: "percentage",
    },
    rawData: item,
    channel_name: item.channel_name as ChannelName,
    has_dynamic_content: item.has_dynamic_segments ?? false,
  };
}

// The counts-based equivalent of transformGroupedMomentAnalytics, for sent_moment_data. No row
// renders the stats array - only the expanded row does, and it loads them itself now - so the
// percentages come from the counts the view aggregates.
export function transformSentMomentData(
  item: SentMomentDataRow,
  index: number,
): TransformedGroupedMomentAnalytics {
  const sends = item.delivery_count + item.error_count + item.presend_error_count;

  return {
    id: index,
    moment_id: item.moment_id,
    moment_grouping_id: item.moment_grouping_id,
    account_id: item.account_id,
    min_timestamp: fromISO8601(item.min_timestamp),
    recipients: makeRecipients(item, sends, item.sole_recipient_data),
    subject: makeSubject(item),
    delivered: {
      value: calculatePercentage(item.delivery_count, sends),
      type: "percentage",
    },
    opened: {
      value: calculatePercentage(item.open_count, item.delivery_count),
      type: "percentage",
    },
    interacted: {
      value: calculatePercentage(
        item.unique_click_count,
        isEmailChannel(item.channel_name) ? item.open_count : item.delivery_count,
      ),
      type: "percentage",
    },
    rawData: item,
    channel_name: item.channel_name as ChannelName,
    has_dynamic_content: item.has_dynamic_segments ?? false,
  };
}

function makeSubject(
  item: Pick<GroupedMomentAnalyticsData, "grouping_info" | "grouping_subject">,
): TransformedGroupedMomentAnalytics["subject"] {
  if (
    item.grouping_info.type === "immediate" ||
    item.grouping_info.type === "unknown" ||
    item.grouping_info.type === "one_off"
  ) {
    return null;
  }

  const interval = formatIntervalFromParts(item.grouping_info.interval);

  const data = { when: `${interval} ${item.grouping_info.event_key}` };

  if (item.grouping_info.type === "person" && item.grouping_info.subject) {
    return {
      ...data,
      subject: `${item.grouping_subject?.first_name} ${item.grouping_subject?.last_name}`,
    };
  }

  return data;
}
function makeRecipients(
  item: Pick<GroupedMomentAnalyticsData, "grouping_info">,
  count: number,
  sole_recipient: SentMomentData["sole_recipient_data"],
): TransformedGroupedMomentAnalytics["recipients"] {
  const { cc, bcc, connection_type } =
    item.grouping_info.type === "unknown"
      ? { cc: [], bcc: [], connection_type: null }
      : item.grouping_info;

  // TODO currently cc and bcc contain the personIds not names
  const baseRecipient = { cc, bcc, connection_type };

  if (count !== 1) {
    return { ...baseRecipient, display: `${count}`, type: "number" };
  }

  if (!sole_recipient) {
    return { ...baseRecipient, display: "-", type: null };
  }

  const [display, type] = sole_recipient.is_multiperson
    ? [`#${sole_recipient.display}`, "channel_name" as const]
    : [`${sole_recipient.first_name} ${sole_recipient.last_name}`, "person_name" as const];

  return { ...baseRecipient, display, type };
}
